// ===========================================
// 📱 COMPONENTE PRINCIPAL: src/components/AGTFin/AGTFin.js
// ===========================================

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Typography, Alert, Container, useTheme, useMediaQuery, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  Settings,
  ChevronRight,
  ChevronLeft
} from 'lucide-react';
import FinancialProfileSetup from '../AGTFin/settings/FinancialProfileSetup';
import FinIncome from '../AGTFin/dashboard/incomes/FinIncome';
import FinBill from '../AGTFin/dashboard/bills/FinBill';
import FinExpense from '../AGTFin/dashboard/expenses/FinExpense';
import FinSavings from '../AGTFin/dashboard/savings/FinSavings';
import FinProducts from '../AGTFin/dashboard/products/FinProducts';
import FinDashboard from '../AGTFin/dashboard/FinDashboard';
import agtFinService from '../AGTFin/services/agtFinService';
import AppleSpinner from '../common/AppleSpinner';
import AGTFloatingChat from './utils/AGTFloatingChat';

// Componentes estilizados
const FinContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(4, 3, 3),
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? 'transparent'
    : (theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF'),
  maxWidth: '1200px',
  minHeight: '100vh', // Asegurar altura mínima completa
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 1, 2),
  }
}));

// Wrapper principal con scroll
const ScrollableWrapper = styled(Box)(({ theme }) => ({
  height: '100vh', // Altura completa de la ventana
  overflowY: 'auto', // Scroll vertical
  overflowX: 'hidden', // Sin scroll horizontal
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? 'transparent'
    : (theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF'),
  // Mejoras para el scroll en móviles
  WebkitOverflowScrolling: 'touch',
  scrollBehavior: 'smooth',
}));

// Título con gradiente (negro/blanco según el tema)
const GradientTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.actualMode === 'wallpaper'
    ? '#FFFFFF'
    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
  display: 'inline-block',
}));

// NavigationBar estilizado - FIJO en la parte superior
const StyledTabs = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    minHeight: '40px',
    height: '40px',
    background: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(40, 40, 42, 0.94)'
        : 'rgba(242, 242, 247, 1)'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    padding: '6px',
    borderRadius: '24px',
    position: 'sticky', // Cambio importante: hacer sticky la barra de navegación
    top: theme.spacing(2), // Separación del top
    zIndex: 100, // Asegurar que esté por encima
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: '500px',
    margin: '0 auto',
    marginBottom: theme.spacing(3),
    border: isWallpaperMode
      ? '1px solid rgba(255, 255, 255, 0.2)'
      : 'none'
  };
});

// TabIndicator
const TabIndicator = styled('div')(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'absolute',
    height: '28px',
    borderRadius: '24px',
    backgroundColor: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.25)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(118, 118, 128, 0.24)'
        : '#FFFFFF'),
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 0,
    boxShadow: isWallpaperMode
      ? 'none'
      : (theme.palette.mode === 'dark'
        ? 'none'
        : '0 2px 8px rgba(0, 0, 0, 0.04)')
  };
});

// StyledTab
const StyledTab = styled('button')(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    minHeight: '28px',
    height: '28px',
    minWidth: 'auto',
    padding: '0 16px',
    borderRadius: '24px',
    border: 'none',
    backgroundColor: 'transparent',
    color: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.8)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.7)'
        : '#666666'),
    fontSize: '14px',
    fontWeight: 500,
    textTransform: 'none',
    zIndex: 1,
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    outline: 'none',
    '&.selected': {
      color: isWallpaperMode
        ? '#FFFFFF'
        : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
      fontWeight: 600,
    },
    '&:hover:not(.selected)': {
      color: isWallpaperMode
        ? '#FFFFFF'
        : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
      opacity: 0.8,
    },
  };
});

const NavigationButton = styled('button')(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    minHeight: '28px',
    height: '28px',
    width: '28px',
    minWidth: '28px',
    padding: '0',
    marginRight: '6px',
    marginLeft: '6px',
    border: 'none',
    borderRadius: '30px',
    backgroundColor: 'transparent',
    color: isWallpaperMode
      ? '#FFFFFF'
      : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 1,
    transition: 'all 0.2s',
    outline: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      opacity: 0.8,
    },
    '&:active': {
      transform: 'scale(0.95)',
      backgroundColor: 'transparent',
    },
  };
});

// Contenedor de contenido con padding extra para scroll
const ContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  paddingBottom: theme.spacing(8), // Espacio extra al final para scroll cómodo
  minHeight: 'calc(100vh - 200px)', // Altura mínima para forzar scroll si es necesario
}));

const AGTFin = ({ user }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const [hasProfile, setHasProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [greeting, setGreeting] = useState("");
  const [showProductsTab, setShowProductsTab] = useState(false);
  // Estados para estadísticas financieras (ejemplo)
  const [financialStats, setFinancialStats] = useState({
    monthlyIncome: { current: 0, goal: 0 },
    expenses: { current: 0, budget: 0 },
    savings: { current: 0, goal: 0 },
    investments: { current: 0, target: 0 }
  });
  const headerMonthlyIncome = useMemo(() => {
    const statsIncome = financialStats?.monthlyIncome?.current;
    if (typeof statsIncome === 'number' && statsIncome > 0) {
      return statsIncome;
    }

    const profileIncome = userProfile?.monthlyIncome;
    if (typeof profileIncome === 'number' && profileIncome > 0) {
      return profileIncome;
    }

    if (typeof profileIncome === 'string') {
      const parsed = Number(profileIncome);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }

    return null;
  }, [financialStats, userProfile]);
  
  // Referencias para los elementos de pestaña (ahora 5 tabs: Ingresos, Cuentas, Gastos, Ahorros, Productos)
  const tabsContainerRef = useRef(null);
  const tabRef0 = useRef(null);
  const tabRef1 = useRef(null);
  const tabRef2 = useRef(null);
  const tabRef3 = useRef(null);
  const tabRef4 = useRef(null);
  const tabRefs = useMemo(() => [tabRef0, tabRef1, tabRef2, tabRef3, tabRef4], []);
  
  // Estado para el indicador de pestañas
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: '6px',
    width: '74px'
  });

  // Generar saludo
  useEffect(() => {
    const generateGreeting = () => {
      let userName = '';

      if (user) {
        if (user.firstName) {
          userName = user.firstName;
        } else if (user.displayName) {
          userName = user.displayName.split(' ')[0];
        } else if (user.email) {
          userName = user.email.split('@')[0];
        }
      }

      if (!userName) {
        userName = user?.uid?.substring(0, 5) || 'm1';
      }

      return t('agtfin.greeting', { userName });
    };

    setGreeting(generateGreeting());
  }, [user, t]);

  // Actualizar la posición del indicador cuando cambia la pestaña activa
  useEffect(() => {
    const updateIndicator = () => {
      if (tabRefs[activeTab]?.current) {
        const tabElement = tabRefs[activeTab].current;
        const tabsContainer = tabsContainerRef.current;

        if (tabElement && tabsContainer) {
          const containerRect = tabsContainer.getBoundingClientRect();
          const tabRect = tabElement.getBoundingClientRect();
          const left = tabRect.left - containerRect.left;

          setIndicatorStyle({
            left: `${left}px`,
            width: `${tabRect.width}px`
          });
        }
      }
    };

    const timer = setTimeout(updateIndicator, 50);
    return () => clearTimeout(timer);
  }, [activeTab, tabRefs]);

  // Verificar si el usuario tiene perfil al cargar el componente
  useEffect(() => {
    const checkUserProfile = async () => {
      if (!user?.uid) {
        setError(t('agtfin.errors.invalidUser'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const profileExists = await agtFinService.checkUserProfile(user.uid);
        
        if (profileExists) {
          const profile = await agtFinService.getUserProfile(user.uid);
          setUserProfile(profile);
          setHasProfile(true);
          
          // Actualizar estadísticas financieras (preferir datos reales del dashboard)
          try {
            const statsResponse = await agtFinService.getFinancialStats();
            if (statsResponse?.financialStats) {
              setFinancialStats(statsResponse.financialStats);
            } else {
              setFinancialStats({
                monthlyIncome: { 
                  current: profile.monthlyIncome || 0, 
                  goal: profile.monthlyIncome || 0 
                },
                expenses: { 
                  current: 0, 
                  budget: profile.monthlyIncome * 0.7 || 0 
                },
                savings: { 
                  current: 0, 
                  goal: profile.savingsGoal || 0 
                },
                investments: { 
                  current: 0, 
                  target: profile.monthlyIncome * 0.2 || 0 
                }
              });
            }
          } catch (statsError) {
            console.error('Error obteniendo estadísticas financieras:', statsError);
            setFinancialStats({
              monthlyIncome: { 
                current: profile.monthlyIncome || 0, 
                goal: profile.monthlyIncome || 0 
              },
              expenses: { 
                current: 0, 
                budget: profile.monthlyIncome * 0.7 || 0 
              },
              savings: { 
                current: 0, 
                goal: profile.savingsGoal || 0 
              },
              investments: { 
                current: 0, 
                target: profile.monthlyIncome * 0.2 || 0 
              }
            });
          }
        } else {
          setHasProfile(false);
        }
      } catch (err) {
        console.error('Error verificando perfil:', err);
        setError(t('agtfin.errors.profileCheckError'));
        setHasProfile(false);
      } finally {
        setLoading(false);
      }
    };

    checkUserProfile();
  }, [user]);

  // Función que se ejecuta cuando se crea el perfil exitosamente
  const handleProfileCreated = async () => {
    try {
      const profile = await agtFinService.getUserProfile(user.uid);
      setUserProfile(profile);
      setHasProfile(true);
      setError('');
    } catch (err) {
      console.error('Error al cargar el perfil creado:', err);
      setError(t('agtfin.errors.profileLoadError'));
    }
  };

  // Manejar cambio de tab
  const handleTabChange = (newValue) => {
    setActiveTab(newValue);
 
    // Si estamos en móvil y cambiamos a Productos, mostrar solo Productos
    if (isMobile && newValue === 4) { // Productos ahora es el índice 4
      setShowProductsTab(true);
    }
 
    // Si estamos en móvil y cambiamos desde Productos a otra pestaña, ocultar Productos
    if (isMobile && showProductsTab && newValue !== 4) {
      setShowProductsTab(false);
    }
  };

  // Pantalla de carga
  if (loading) {
    return (
      <ScrollableWrapper>
        <Box sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2
        }}>
          <AppleSpinner size={50} />
          <Typography variant="h6" color="text.secondary">
            {t('agtfin.loading')}
          </Typography>
        </Box>
      </ScrollableWrapper>
    );
  }

  // Pantalla de error
  if (error) {
    return (
      <ScrollableWrapper>
        <Box sx={{
          height: '100%',
          width: '100%',
          padding: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Alert severity="error" sx={{ mb: 2, width: '100%', maxWidth: 400 }}>
            {error}
          </Alert>
          <Typography variant="body1">
            {t('agtfin.error')}
          </Typography>
        </Box>
      </ScrollableWrapper>
    );
  }

  // Si no tiene perfil, mostrar configuración
  if (hasProfile === false) {
    return (
      <ScrollableWrapper>
        <FinancialProfileSetup 
          user={user} 
          onProfileCreated={handleProfileCreated}
        />
      </ScrollableWrapper>
    );
  }

  // Definición de las pestañas (con Cuentas añadida)
  const tabs = [
    { id: 0, label: t('agtfin.navigation.incomes') },
    { id: 1, label: t('agtfin.navigation.bills') },
    { id: 2, label: t('agtfin.navigation.expenses') },
    { id: 3, label: t('agtfin.navigation.savings') },
    { id: 4, label: t('agtfin.navigation.products') }
  ];

  // Renderizar contenido según la pestaña activa
  const renderTabContent = () => {
    switch (activeTab) {
      case 0: // Ingresos
        return <FinIncome user={user} userProfile={userProfile} />;

      case 1: // Cuentas (nuevo)
        return <FinBill user={user} userProfile={userProfile} />;

      case 2: // Gastos (índice actualizado)
        return <FinExpense user={user} userProfile={userProfile} />;

      case 3: // Ahorros (índice actualizado) - NUEVO COMPONENTE
        return <FinSavings user={user} userProfile={userProfile} />;

      case 4: // Productos (índice actualizado)
        return <FinProducts user={user} userProfile={userProfile} />;

      default:
        return null;
    }
  };

  // Si tiene perfil, mostrar la aplicación principal
  return (
    <ScrollableWrapper>
      <FinContainer>
        {/* Header con saludo y configuración */}
        <Box sx={{ 
          mb: 4, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: isMobile ? 'nowrap' : 'wrap'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <GradientTitle variant={isMobile ? "h5" : "h4"} sx={{ mb: 0 }}>
              {greeting}
            </GradientTitle>
          </Box>
          
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            ml: isMobile ? 'auto' : 0
          }}>
            <IconButton
              sx={{
                bgcolor: theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(128, 128, 128, 0.3)'
                  : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'),
                backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                border: theme.palette.actualMode === 'wallpaper'
                  ? '1px solid rgba(255, 255, 255, 0.2)'
                  : 'none',
                p: 1.5,
                color: theme.palette.actualMode === 'wallpaper'
                  ? '#FFFFFF'
                  : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000')
              }}
            >
              <Settings size={20} />
            </IconButton>
          </Box>
        </Box>
        
        {/* Información del objetivo */}
        {!isMobile && userProfile && (
          <Typography variant="body1" color="text.secondary" sx={{ mt: -3, mb: 3 }}>
            {headerMonthlyIncome !== null && (
              <>
                {t('agtfin.monthlyIncome')} {userProfile.currency} {headerMonthlyIncome}
                {' • '}
              </>
            )}
            {userProfile.savingsGoal && (
              <>
                {t('agtfin.savingsGoal')} {userProfile.currency} {userProfile.savingsGoal}
              </>
            )}
          </Typography>
        )}
        
        {/* Dashboard siempre visible */}
        <FinDashboard 
          financialStats={financialStats}
          userProfile={userProfile}
        />
        
        {/* NavigationBar - Ahora sticky */}
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <StyledTabs ref={tabsContainerRef}>
            <TabIndicator style={indicatorStyle} />
   
            {/* En móvil, mostrar comportamiento especial para Productos */}
            {isMobile ? (
              <>
                {/* Si Productos está activo, mostrar solo Productos con chevron izquierdo */}
                {showProductsTab && activeTab === 4 ? ( // Productos ahora es índice 4
                  <>
                    <NavigationButton 
                      onClick={() => {
                        setShowProductsTab(false);
                        handleTabChange(0);
                      }}
                    >
                      <ChevronLeft size={20} />
                    </NavigationButton>
   
                    <StyledTab
                      key={tabs[4].id}
                      onClick={() => handleTabChange(tabs[4].id)}
                      className={activeTab === tabs[4].id ? 'selected' : ''}
                      ref={tabRefs[4]}
                    >
                      {tabs[4].label}
                    </StyledTab>
                  </>
                ) : (
                  <>
                    {/* Mostrar las 4 pestañas: Ingresos, Cuentas, Gastos, Ahorros */}
                    {tabs.slice(0, 4).map((tab, index) => (
                      <StyledTab 
                        key={tab.id}
                        onClick={() => handleTabChange(tab.id)}
                        className={activeTab === tab.id ? 'selected' : ''}
                        ref={tabRefs[index]}
                      >
                        {tab.label}
                      </StyledTab>
                    ))}
   
                    {/* Chevron derecho para mostrar Productos */}
                    <NavigationButton 
                      onClick={() => {
                        setShowProductsTab(true);
                        handleTabChange(4); // Productos ahora es índice 4
                      }}
                    >
                      <ChevronRight size={20} />
                    </NavigationButton>
                  </>
                )}
              </>
            ) : (
              // En escritorio, mostrar todas las pestañas
              tabs.map((tab, index) => (
                <StyledTab 
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={activeTab === tab.id ? 'selected' : ''}
                  ref={tabRefs[index]}
                >
                  {tab.label}
                </StyledTab>
              ))
            )}
          </StyledTabs>
        </Box>

        {/* Contenido de las pestañas con contenedor scrolleable */}
        <ContentContainer>
          {renderTabContent()}
        </ContentContainer>
      </FinContainer>

      <AGTFloatingChat user={user} />
    </ScrollableWrapper>
  );
};

export default AGTFin;
